import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import StarUnstarredIcon from '@atlaskit/icon/core/star-unstarred';
import StarStarredIcon from '@atlaskit/icon/core/star-starred';
import { Box } from '@atlaskit/primitives';
import { cssMap, cx } from '@atlaskit/css';
import { IconButton } from '@atlaskit/button/new';
import { token } from '@atlaskit/tokens';

import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import {
	ANALYTICS_ACTION,
	ANALYTICS_SOURCE,
	ANALYTICS_ORIGIN,
	ANALYTICS_SUBJECT,
	ANALYTICS_SUBJECT_ID,
} from '../constants/snippetAnalytics.ts';
import type { Snippet } from '../snippetTypes.ts';
import { useSnippetsActions } from '../store/snippetsStore.ts';

export interface SnippetPanelFavoriteButtonProps {
	snippet: Snippet;
	isHovered: boolean;
}

const styles = cssMap({
	container: {
		flexShrink: 0,
		opacity: 0,
		transition: 'opacity 0.15s ease-in-out',

		'&:focus-within': {
			opacity: 1,
		},
	},
	containerVisible: {
		opacity: 1,
	},
});

const messages = defineMessages({
	favoritedLabel: {
		id: 'snippets.snippetPanelFavoriteButton.favoritedLabel',
		defaultMessage: 'Unfavorite Snippet',
		description: 'Label for the favorite button when a snippet is favorited',
	},
	unfavoritedLabel: {
		id: 'snippets.snippetPanelFavoriteButton.unfavoritedLabel',
		defaultMessage: 'Favorite Snippet',
		description: 'Label for the favorite button when a snippet is not favorited',
	},
});

const SnippetPanelFavoriteButtonInner = ({
	snippet,
	isHovered,
	flags,
}: SnippetPanelFavoriteButtonProps & WithFlagsProps) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const snippetsActions = useSnippetsActions();
	const shouldShowIcon = snippet.isFavorite || isHovered;
	const options = {
		intl,
		flags,
	};

	const handleFavoriteToggle = (isFavoriteAfterToggle: boolean) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: ANALYTICS_SOURCE,
				actionSubject: ANALYTICS_SUBJECT.BUTTON,
				action: ANALYTICS_ACTION.CLICKED,
				actionSubjectId: ANALYTICS_SUBJECT_ID.FAVORITE_TOGGLED,
				type: 'ui',
				attributes: {
					origin: ANALYTICS_ORIGIN.SNIPPET_PANEL,
					snippetId: snippet.snippetId,
					scope: snippet.scope,
					isFavoriteAfterToggle,
				},
			},
		}).fire();

		if (isFavoriteAfterToggle) {
			void snippetsActions.favoriteSnippet(snippet.snippetId, options);
		} else {
			void snippetsActions.unfavoriteSnippet(snippet.snippetId, options);
		}
	};

	return (
		<Box xcss={cx(styles.container, shouldShowIcon && styles.containerVisible)}>
			{snippet.isFavorite ? (
				<IconButton
					label={intl.formatMessage(messages.favoritedLabel)}
					icon={(iconProps) => (
						<StarStarredIcon {...iconProps} color={token('color.icon.accent.yellow')} label="" />
					)}
					appearance="subtle"
					onClick={() => handleFavoriteToggle(false)}
				/>
			) : (
				<IconButton
					label={intl.formatMessage(messages.unfavoritedLabel)}
					icon={StarUnstarredIcon}
					appearance="subtle"
					onClick={() => handleFavoriteToggle(true)}
				/>
			)}
		</Box>
	);
};

export const SnippetPanelFavoriteButton = withFlags(SnippetPanelFavoriteButtonInner);
